import {
  getDefaultBotAvatar,
  getDefaultUserAvatar,
} from "./avatars.js";
import { getTheme } from "../../../core/configAccessors.js";
import { emit } from "../../../core/state.js";
import { renderInlineMarkdown } from "../../../services/markdown.js";

const INFO_ICON = `<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm.75 15h-1.5v-6h1.5v6zm0-8h-1.5V7.5h1.5V9z"/></svg>`;

export default class TextBubble {
  render(message) {
    const role = message.role || "bot";
    const isWelcome = message.meta?.isWelcome === true;

    const row = document.createElement("div");
    row.className = role === "user" ? "ws-user-row" : "ws-bot-row";
    if (isWelcome) row.classList.add("ws-welcome-row");
    if(message.isFirstResponse) row.classList.add("ws-fr");

    const avatarUrl =
      message.meta?.avatarUrl ||
      (role === "user"
        ? getTheme("user_avatar_url", null)
        : getTheme("bot_avatar_url", null));

    const avatar = this._buildAvatar(role, avatarUrl);

    const bubbleWrap = document.createElement("div");
    bubbleWrap.className = "ws-bubble-wrap";

    const bubble = document.createElement("div");
    bubble.className =
      role === "user" ? "ws-user-bubble" : "ws-bot-bubble";
    if (isWelcome) bubble.classList.add("ws-welcome");
    const text = message.data?.text || "";
    const typeMessage = message.data?.type;
    if (role === "bot") {
      bubble.innerHTML = renderInlineMarkdown(text, typeMessage);
      this._wireActionLinks(bubble);
    } else {
      // Parity: user messages over 50 words are truncated with a "Show More"
      // toggle (old truncateText/addMessage, script.js 2318-2439).
      const words = text.split(/\s+/);
      if (words.length > 50) {
        bubble.textContent = words.slice(0, 50).join(" ") + "...";
        const more = document.createElement("span");
        more.className = "ws-show-more";
        more.textContent = "Show More";
        more.addEventListener("click", () => {
          bubble.textContent = text;
        });
        bubble.appendChild(more);
      } else {
        bubble.textContent = text;
      }
    }
    bubbleWrap.appendChild(bubble);

    if (role === "bot" && message.meta?.showInfoIcon && message.meta?.infoText) {
      this._attachInfoIcon(bubble, bubbleWrap, {
        title: message.meta.infoTitle,
        text: message.meta.infoText,
      });
    }

    if (role === "user" && message.meta?.drawerWine) {
      const reply = document.createElement("div");
      reply.className = "ws-drawer-context-reply";

      const ctxBubble = document.createElement("div");
      ctxBubble.className = "ws-drawer-context-bubble";

      const tab = message.meta.drawerTab || "Tasting Notes";
      const labelSpan = document.createElement("span");
      labelSpan.className = "ws-drawer-context-label";
      labelSpan.textContent = `From ${tab}`;
      ctxBubble.appendChild(labelSpan);
      ctxBubble.appendChild(document.createTextNode(` · ${message.meta.drawerWine}`));

      reply.appendChild(ctxBubble);

      const openPayload = message.meta.drawerOpenPayload;
      if (openPayload?.uniqueId) {
        reply.addEventListener("click", () => {
          emit("ws-wine-drawer-open", openPayload);
        });
      }

      bubbleWrap.insertBefore(reply, bubble);
    }

    if (role === "user") {
      row.appendChild(bubbleWrap);
      row.appendChild(avatar);
    } else {
      row.appendChild(avatar);
      row.appendChild(bubbleWrap);
    }

    return row;
  }

  _buildAvatar(role, url) {
    const wrap = document.createElement("div");
    wrap.className =
      role === "user" ? "ws-avatar ws-user-avatar" : "ws-avatar ws-bot-avatar";

    if (url) {
      const img = document.createElement("img");
      img.src = url;
      img.alt = "";
      img.onerror = () => {
        wrap.innerHTML =
          role === "user" ? getDefaultUserAvatar() : getDefaultBotAvatar();
      };
      wrap.appendChild(img);
    } else {
      wrap.innerHTML =
        role === "user" ? getDefaultUserAvatar() : getDefaultBotAvatar();
    }

    return wrap;
  }

  // Bot bubbles may contain inline action links written as markdown with a
  // `ws-action:<value>` href (e.g. [Text a Live Person](ws-action:__text_live_person__)).
  // Clicking one fires the same `ws-prompt-select` event a guided chip does, so
  // any variant's existing prompt handling runs — no navigation. Shared by all wineries.
  _wireActionLinks(bubble) {
    bubble.addEventListener("click", (e) => {
      const path = e.composedPath ? e.composedPath() : [];
      const link = path.find(
        (el) => el && el.tagName === "A" && (el.getAttribute("href") || "").startsWith("ws-action:"),
      );
      if (!link) return;
      e.preventDefault();
      e.stopPropagation();
      const value = link.getAttribute("href").slice("ws-action:".length);
      if (value) emit("ws-prompt-select", { label: link.textContent || "", value });
    });
  }

  _attachInfoIcon(bubble, wrap, info) {
    const title = typeof info === "object" ? info.title : null;
    const text = typeof info === "object" ? info.text : info;

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ws-info-icon";
    btn.setAttribute("aria-label", "More info");
    btn.innerHTML = INFO_ICON;
    bubble.appendChild(btn);

    let popover = null;
    let outsideHandler = null;

    const close = () => {
      if (!popover) return;
      popover.remove();
      popover = null;
      if (outsideHandler) {
        document.removeEventListener("click", outsideHandler, true);
        outsideHandler = null;
      }
    };

    const open = () => {
      popover = document.createElement("div");
      popover.className = "ws-info-popover";
      if (title && String(title).trim()) {
        const titleEl = document.createElement("div");
        titleEl.className = "ws-info-popover-title";
        titleEl.textContent = title;
        popover.appendChild(titleEl);
      }
      const body = document.createElement("div");
      body.className = "ws-info-popover-body";
      body.innerHTML = renderInlineMarkdown(text);
      popover.appendChild(body);
      wrap.appendChild(popover);

      outsideHandler = (e) => {
        const path = e.composedPath ? e.composedPath() : [];
        if (path.includes(popover) || path.includes(btn)) return;
        close();
      };
      setTimeout(() => {
        document.addEventListener("click", outsideHandler, true);
      }, 0);
    };

    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (popover) close();
      else open();
    });
  }
}
